import get from 'lodash/get';

// @tcc
import { UnitQty, numberFormatter } from '@tcc/i18n';

// constants
import { fertilityProductFormats } from 'constants/products';
import { UNIT_TYPES } from 'constants/unitTypes';
import { applicationProperties, scriptTypes } from 'constants/scripts';

// util
import { getProductRateUnitTypeByUnit } from 'util/unit';
import { isCommaDecimalLang } from 'constants/countryCodes';
import { getLocaleLang } from 'util/locale';

/**
 * @typedef {Object} Unit
 * @property {Number} scalar - Unrounded, unlocalized scalar value
 * @property {String} scalarLocaleString - Rounded, localized scalar value
 * @property {String} originalUnitKey - Unit key passed in (before being potentially aliased)
 * @property {String} unitKey - Unit key (potentially aliased)
 * @property {Object} symbol
 * @property {String} symbol.universal
 * @property {String} symbol.quantitySpecific
 * @property {Object} label
 * @property {String} label.universal
 * @property {String} label.quantitySpecific
 * @property {Function} toString
 * @property {Function} to
 * @property {Function} forLocaleGroup
 * @property {Object} _unitTemplate
*/

// TODO CFVWEB-4352 Move everything unit-related to unit-qty. The only calls to unit-qty
// should be through dumb wrapper functions.

// default to imperial, US while initialization is happening
let unitQty = new UnitQty('imperial', 'US');

/**
 * Creates a new qtyFactory object based on the user's settings
 * @param {String} unitTemplate
 * @param {String} countryCode
 */
export const initializeUnitQty = (unitTemplate, countryCode) => {
  unitQty = new UnitQty(unitTemplate, countryCode);
};

/**
 * Wrapper for unitQty's inferUnit
 * @param {string} displayUnitType
 * @param {number} [scalar]
 * @returns {Unit}
 */
export const inferUnit = (displayUnitType, scalar) =>
  unitQty.inferUnit(displayUnitType, scalar);

/**
 * Wrapper for unitQty's createUnit
 * @param {number} scalar
 * @param {string} unitKey
 * @param {number} [precision]
 * @returns {Unit}
 */
export const createUnit = (scalar, unitKey, precision) => {
  return unitQty.createUnit(scalar, unitKey, precision);
};

/**
 *
 * @param {Object} Unit
 * @param {Number} Unit.quantity
 * @param {String} Unit.unit
 * @param {String} Unit.unitType
 * @param {Number} [Unit.cropNominalWeight]
 * @returns {Unit}
 */
export const convertForLocale = ({ quantity, precision, unit, unitType, cropNominalWeight }) =>
  createUnit(quantity, unit, precision).forLocaleGroup(unitType, cropNominalWeight);

/**
 * Converts if quantity is 0 or truthy, otherwise returns null
 * @param {Object} Unit
 * @param {Number} Unit.quantity
 * @param {String} Unit.unit
 * @param {String} Unit.unitType
 * @param {Number} [Unit.cropNominalWeight]
 * @returns {Number|null}
 */
export const convertForLocaleScalar = ({ quantity, unit, unitType, cropNominalWeight }) => {
  return quantity === 0 || quantity ? convertForLocale({
    quantity, unit, unitType, cropNominalWeight }).scalar : null;
};

const DEFAULT_UNIT_PRECISION = 0;
const unitPrecison = {
  [UNIT_TYPES.usd_acre]:2,
  [UNIT_TYPES.usd_hectare]:2
};
const _getUnitPrecision = (unitType) => unitPrecison[unitType] || DEFAULT_UNIT_PRECISION;

const _unitQtyTo = ({from, toUnit, precision, unitQtyToOptions}) => {

  /*  If unitQty doesn't accept the "from" or "to" unit type due to a difference in naming, (e.g. we say "bu", and
      unitQty wants "bushel") then update the unitQty project to include a new unit key mapping, instead of adding
      a mapping in our code. The idea is to avoid maintaining a mapping layer in our project, and for multiple
      projects to benefit from a mapping layer maintained in unitQty. I also don't recommend changing our unit types
      to match unitQty's if they are already corresponding to units commonly used by backend services. I think its
      less work to maintain and fix bugs if our units mostly match what services use, and unitQty is given aliases
      (unit key mappings) to deal with them. */
  const to = unitQty.createUnit(from.q, from.u).to(toUnit, { ...unitQtyToOptions, precision });
  return { q: to.scalar, u: to.originalUnitKey };

};

/** Convert from one unit to another in cases where no unit-specific handling is needed.
    @param from Unit value with .q and .u.
    @param toUnit To unit type.
    @param precision (optional) Number of digits to right of decimal point to include.
    @returns {{q: Number, u: (*|string|String)}}
    @throws Error if unit types are unknown or requested conversion is not supported. */
export const toUnit = ({from, toUnit, precision = _getUnitPrecision(toUnit)}) => _unitQtyTo({from, toUnit, precision});

/** See crops/selectors.js for getting the nominal weight value.
    @param from Unit value with .u of one of the weight-per-area types, e.g. 'kg/hectare'.
    @param string toUnit Weight-per-area unit type.
    @param number cropNominalWeight Just the quantity value. Unit is implied by toUnit. 
    @returns {{q: Number, u: (*|string|String)}}  */
export const convertYieldWithNominalWeight = ({from, toUnit, cropNominalWeight}) => {
  // TODO whenever bu to mass conversions are added to unit-qty, remove this work around
  if(from.u === UNIT_TYPES.bu && toUnit === UNIT_TYPES.tonne) {
    return {q: (from.q * cropNominalWeight) / 2204.62, u: UNIT_TYPES.tonne};
  }
  return _unitQtyTo({from, toUnit, precision:_getUnitPrecision(toUnit), unitQtyToOptions:{ cropNominalWeight }});
};

/** Convert from one unit to another.
    @param options.from.u
    @param options.from.q
    @param options.to.u
    @returns {{q: Number, u: (string|String)}|null}
 */
export const convert = (options = {}) => {
  const { cropNominalWeight, precision } = options;

  const from = Object.assign({}, options.from);
  const convertToUnit = get(options, 'to.u');

  // TODO Add this mapping to unit-qty
  from.u = from.u === 'liter/hectare' ? 'l/ha' : from.u;

  // used in advanced scripting hank request -- grain price
  if(from.u.includes('usd/') && convertToUnit === UNIT_TYPES.usd_kg) {
    const massYieldUnit = from.u.split('/')[1];
    const metricTonsOfCornPerBushel = convert({
      from: { q: 1, u: massYieldUnit },
      to: { u: UNIT_TYPES.tonne },
      cropNominalWeight,
      precision: 5
    });

    const kilogramsOfCornPerBushel = convert({
      from: { q: metricTonsOfCornPerBushel.q, u: UNIT_TYPES.metric_ton },
      to: { u: UNIT_TYPES.kilogram },
      precision: 5
    });
    return { q: from.q * (1/kilogramsOfCornPerBushel.q), u: convertToUnit };
  }

  if (cropNominalWeight) {
    return convertYieldWithNominalWeight({ from, toUnit: convertToUnit, cropNominalWeight });
  }

  return toUnit({ from, toUnit: convertToUnit, precision });
};

export const convertByFormat = ({ format, from }) => {
  switch (format) {
    case fertilityProductFormats.DRY:
      return convert({ from, to: { u: 'kg/hectare' } }); // groot doesn't accept standard kilogram/hectare
    case fertilityProductFormats.WET:
      return convert({ from, to: { u: UNIT_TYPES.l_ha } });
    default:
      return null;
  }
};

/**
 * Converts a fertilizer summary for the user's locale
 * @param {Object} fertilizerSummary
 * @param {Number} fertilizerSummary.avgRate.q
 * @param {String} fertilizerSummary.avgRate.u
 * @param {Number} fertilizerSummary.totalProduct.q
 * @param {String} fertilizerSummary.totalProduct.u
 * @param {Object} [fertilizerSummary.avgNitrogenRate]
 * @param {Number} fertilizerSummary.avgNitrogenRate.q
 * @param {String} fertilizerSummary.avgNitrogenRate.u
 * @returns {{convertedFertilizerRate: *, convertedTotalProduct: *, convertedAvgNitrogenRate: *}}
 */
export const convertFertilizerSummaryForLocale = (fertilizerSummary = {}) => {
  const convertedFertilizerRate = fertilizerSummary.avgRate
    && {
      convertedFertilizerRate: convertForLocale({
        quantity: fertilizerSummary.avgRate.q,
        unit: fertilizerSummary.avgRate.u,
        unitType: getProductRateUnitTypeByUnit(fertilizerSummary.avgRate.u)
      })
    };

  const convertedTotalProduct = fertilizerSummary.totalProduct
    && {
      convertedTotalProduct: convertForLocale({
        quantity: fertilizerSummary.totalProduct.q,
        unit: fertilizerSummary.totalProduct.u,
        unitType: fertilizerSummary.totalProduct.u === UNIT_TYPES.gal // cannot use getProductRateUnitTypeByUnit because it is not a rate
          ? 'volume'
          : 'mass.application.dry-fertilizer',
        precision: fertilizerSummary.precision
      })
    };

  const convertedAvgNitrogenRate = fertilizerSummary.avgNitrogenRate
    && {
      convertedAvgNitrogenRate: convertForLocale({
        quantity: fertilizerSummary.avgNitrogenRate.q,
        unit: fertilizerSummary.avgNitrogenRate.u,
        unitType: 'area-density.application'
      })
    };

  return {
    ...convertedAvgNitrogenRate,
    ...convertedFertilizerRate,
    ...convertedTotalProduct
  };
};

export const convertFertilityRateForLocale = (
  rawNitrogenRate, rawFertilizerRateValue, scriptTypeIsNitrogen, scriptType) => {

  let rawFertilizerRate = !rawFertilizerRateValue
    ? { rate: { q: null, u: 'gal/ac' }}
    : rawFertilizerRateValue;

  const convertedRawFertilizerRate = convertForLocaleScalar({
    quantity: rawFertilizerRate.q,
    unit: rawFertilizerRate.u,
    unitType: getProductRateUnitTypeByUnit(rawFertilizerRate.u)
  });
  if (scriptTypeIsNitrogen) {
    const convertedRawNitrogenRate = convertForLocaleScalar({
      quantity: rawNitrogenRate.q,
      unit: rawNitrogenRate.u,
      unitType: 'area-density.application'
    });

    return {
      rate: convertedRawNitrogenRate,
      rateKey: applicationProperties.NITROGEN_RATE,
      convertedRawFertilizerRate: convertedRawFertilizerRate
    };
  }
  return {
    rate: convertedRawFertilizerRate,
    rateKey: scriptType === scriptTypes.APPLICATION
      ? applicationProperties.RATE
      : applicationProperties.FERTILIZER_RATE,
    convertedRawFertilizerRate: convertedRawFertilizerRate
  };
};

export const parseDecimals = (scalarString) => {
  let num = '';
  if(isCommaDecimalLang(getLocaleLang())) {
    num = Number(scalarString.replace(/[.\u202f\u00a0\u0020]/g, '').replace(/,/g, '.'));
  } else {
    num = Number(scalarString.replace(/,/g, ''));
  }
  return numberFormatter(num, { precision: 2 });
};

/**
 * Converts a seeding rate value to a target unit, and returns it in the Measure type used by the UAPI
 *
 * @param {string} value - The seeding rate value to convert.
 * @param {string} unit - The unit to convert to.
 * @returns {{v: number, u: string}} The converted seeding rate value in the target unit in the Measure.
 */
export const convertSeedingRateTo = (value, unit) => {
  const { q, u } = convert({
    from: { q: Number(value), u: inferUnit('seed-density').unitKey },
    to: { u: unit }
  });

  return { v: q, u };
};

